import axios, { AxiosInstance, AxiosResponse, AxiosRequestConfig } from 'axios';
import { useAuthStore } from '../store/authStore';
import { SignupCredentials } from '../types';
import { mockApi } from './mockApi';
import { cacheService, CacheConfig } from './cache.service';
import { memoizeRequest } from '../utils/memoizeRequest';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://panel-production-038c.up.railway.app/api';
const USE_MOCK_API = false; // Disable mock API - use real backend

// Helper to create mock AxiosResponse
const createMockResponse = <T>(data: T): AxiosResponse<T> => ({
  data,
  status: 200,
  statusText: 'OK',
  headers: {},
  config: {} as any,
});

class ApiService {
  private api: AxiosInstance;
  
  // Memoized methods
  public getUsersMemoized: typeof ApiService.prototype.getUsers;
  public getOnlineUsersMemoized: typeof ApiService.prototype.getOnlineUsers;
  public getProjectsMemoized: typeof ApiService.prototype.getProjects;
  public getTasksMemoized: typeof ApiService.prototype.getTasks;

  
  constructor() {
    // Log API configuration in development
    if (process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost') {
      console.log('API Service initialized with base URL:', API_BASE_URL);
    }
    
    this.api = axios.create({
      baseURL: API_BASE_URL,
      timeout: 15000, // 15 second timeout
      headers: {
        'Content-Type': 'application/json',
        // Accept-Encoding is automatically handled by the browser
      },
    });

    this.setupInterceptors();
    this.setupMemoizedMethods();
  }
  
  private setupMemoizedMethods() {
    // Memoize frequently called methods with 10 second TTL
    this.getUsersMemoized = memoizeRequest(
      this.getUsers.bind(this),
      { ttl: 10000 }
    );
    
    this.getOnlineUsersMemoized = memoizeRequest(
      this.getOnlineUsers.bind(this),
      { ttl: 5000 } // Shorter TTL for online status
    );
    
    this.getProjectsMemoized = memoizeRequest(
      this.getProjects.bind(this),
      { ttl: 10000 }
    );
    
    this.getTasksMemoized = memoizeRequest(
      this.getTasks.bind(this),
      { ttl: 10000 }
    );
  }

  private setupInterceptors() {
    // Request interceptor to add auth token and handle cache
    this.api.interceptors.request.use(
      async (config) => {
        const { accessToken } = useAuthStore.getState();
        if (accessToken) {
          config.headers.Authorization = `Bearer ${accessToken}`;
        }

        // Check cache for GET requests
        if (config.method?.toUpperCase() === 'GET' && !config.headers?.['X-Bypass-Cache']) {
          const cacheKey = cacheService.generateCacheKey(config);
          if (cacheKey) {
            const cachedEntry = cacheService.get(cacheKey);
            if (cachedEntry) {
              // Return cached response
              config.adapter = async () => {
                return Promise.resolve({
                  data: cachedEntry.data,
                  status: 200,
                  statusText: 'OK',
                  headers: {
                    ...cachedEntry.headers,
                    'X-Cache-Hit': 'true',
                  },
                  config,
                  request: {},
                });
              };
            } else {
              // Add cache key to config for response interceptor
              (config as any).cacheKey = cacheKey;
            }
          }
        }

        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor to handle token refresh and caching
    this.api.interceptors.response.use(
      (response) => {
        // Cache successful GET responses
        const config = response.config as any;
        if (config.method?.toUpperCase() === 'GET' && config.cacheKey && response.status === 200) {
          cacheService.set(config.cacheKey, response.data, config.cacheConfig);
        }

        // Invalidate cache for mutating operations
        if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(config.method?.toUpperCase() || '')) {
          cacheService.invalidateByOperation(config.method!, config.url!);
        }

        return response;
      },
      async (error) => {
        // Log errors in development
        if (process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost') {
          console.error('API Error:', {
            url: error.config?.url,
            method: error.config?.method,
            status: error.response?.status,
            data: error.response?.data,
            message: error.message
          });
        }
        
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            const { refreshToken } = useAuthStore.getState();
            if (refreshToken) {
              const response = await this.refreshToken(refreshToken);
              const { accessToken: newAccessToken, refreshToken: newRefreshToken } = response.data;
              
              useAuthStore.getState().setAuth(
                useAuthStore.getState().user!,
                newAccessToken,
                newRefreshToken
              );

              originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
              return this.api(originalRequest);
            }
          } catch (refreshError) {
            useAuthStore.getState().clearAuth();
            window.location.href = '/auth/login';
          }
        }

        return Promise.reject(error);
      }
    );
  }

  // Auth endpoints
  async login(email: string, password: string) {
    if (USE_MOCK_API) {
      try {
        const result = await mockApi.login(email, password);
        return { data: result };
      } catch (error: any) {
        throw { response: { data: { message: error.message } } };
      }
    }
    return this.api.post('/auth/login', { email, password });
  }

  async signup(userData: SignupCredentials) {
    if (USE_MOCK_API) {
      try {
        const result = await mockApi.signup(userData);
        return { data: result };
      } catch (error: any) {
        throw { response: { data: { message: error.message } } };
      }
    }
    return this.api.post('/auth/signup', userData);
  }

  async logout() {
    return this.api.post('/auth/logout');
  }

  async refreshToken(refreshToken: string) {
    return this.api.post('/auth/refresh', { refreshToken });
  }

  async getCurrentUser() {
    return this.api.get('/auth/me');
  }
  
  async getUserAvatar() {
    return this.api.get('/auth/avatar');
  }

  async forgotPassword(email: string) {
    return this.api.post('/auth/forgot-password', { email });
  }

  async resetPassword(token: string, password: string) {
    return this.api.post('/auth/reset-password', { token, password });
  }

  // Users endpoints
  async getUsers(params?: { limit?: number; page?: number }) {
    if (USE_MOCK_API) {
      return createMockResponse([]);
    }
    return this.api.get('/users', { params });
  }

  async getOnlineUsers() {
    if (USE_MOCK_API) {
      return createMockResponse([]);
    }
    return this.api.get('/users/online');
  }

  async getUser(id: string) {
    return this.api.get(`/users/${id}`);
  }

  async updateUser(id: string, userData: any) {
    return this.api.patch(`/users/${id}`, userData);
  }

  async updateUserRole(id: string, role: string) {
    return this.api.patch(`/users/${id}/role`, { role });
  }

  async deleteUser(id: string) {
    return this.api.delete(`/users/${id}`);
  }

  async updateUserHourlyRate(id: string, hourlyRate: number) {
    return this.api.patch(`/users/${id}/hourly-rate`, { hourlyRate });
  }

  async getUserProjects(userId: string) {
    return this.api.get(`/users/${userId}/projects`);
  }

  // Teams endpoints
  async getTeams() {
    return this.api.get('/teams');
  }

  async getTeam(id: string) {
    return this.api.get(`/teams/${id}`);
  }

  async createTeam(teamData: any) {
    return this.api.post('/teams', teamData);
  }

  async updateTeam(id: string, teamData: any) {
    return this.api.patch(`/teams/${id}`, teamData);
  }

  async deleteTeam(id: string) {
    return this.api.delete(`/teams/${id}`);
  }

  // Projects endpoints
  async getProjects() {
    if (USE_MOCK_API) {
      return createMockResponse([]);
    }
    return this.api.get('/projects');
  }

  async getProject(id: string) {
    return this.api.get(`/projects/${id}`);
  }

  async createProject(projectData: any) {
    return this.api.post('/projects', projectData);
  }

  async updateProject(id: string, projectData: any) {
    return this.api.patch(`/projects/${id}`, projectData);
  }

  async deleteProject(id: string) {
    return this.api.delete(`/projects/${id}`);
  }

  async addProjectMember(projectId: string, userId: string) {
    return this.api.post(`/projects/${projectId}/members/${userId}`);
  }

  async removeProjectMember(projectId: string, userId: string) {
    return this.api.delete(`/projects/${projectId}/members/${userId}`);
  }

  async uploadProjectAvatar(projectId: string, file: File) {
    const formData = new FormData();
    formData.append('file', file);
    return this.request({
      method: 'POST',
      url: `/projects/${projectId}/upload-avatar`,
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  // Tasks endpoints
  async getTasks(options?: { projectId?: string; assigneeId?: string; limit?: number; page?: number } | string) {
    // Handle legacy single parameter calls
    if (typeof options === 'string') {
      return this.api.get('/tasks', { params: { projectId: options } });
    }
    
    const params: any = {};
    if (options?.projectId) params.projectId = options.projectId;
    if (options?.assigneeId) params.assigneeId = options.assigneeId;
    if (options?.limit) params.limit = options.limit;
    if (options?.page) params.page = options.page;
    return this.api.get('/tasks', { params });
  }

  async getTask(id: string) {
    return this.api.get(`/tasks/${id}`);
  }

  async createTask(taskData: any) {
    console.log('Creating task with data:', taskData);
    return this.api.post('/tasks', taskData);
  }

  async updateTask(id: string, taskData: any) {
    return this.api.patch(`/tasks/${id}`, taskData);
  }

  async deleteTask(id: string) {
    return this.api.delete(`/tasks/${id}`);
  }

  async assignTask(taskId: string, userId: string) {
    return this.api.patch(`/tasks/${taskId}/assign`, { userId });
  }

  async updateTaskStatus(taskId: string, status: string) {
    return this.api.patch(`/tasks/${taskId}/status`, { status });
  }

  async getTaskBoards(projectId?: string) {
    const params = projectId ? { projectId } : {};
    return this.api.get('/tasks/boards', { params });
  }

  async createTaskBoard(data: any) {
    return this.api.post('/tasks/boards', data);
  }

  async getTaskBoard(id: string) {
    return this.api.get(`/tasks/boards/${id}`);
  }

  async addTaskComment(taskId: string, comment: string) {
    return this.api.post(`/tasks/${taskId}/comments`, { content: comment });
  }

  async getTaskComments(taskId: string) {
    return this.api.get(`/tasks/${taskId}/comments`);
  }

  // CRM endpoints
  async getCRMOpportunities(filters?: { userId?: string; month?: string }) {
    const params: any = {};
    if (filters?.userId) params.userId = filters.userId;
    if (filters?.month) params.month = filters.month;
    return this.api.get('/crm/opportunities', { params });
  }

  async getCRMOpportunitiesByStage() {
    return this.api.get('/crm/opportunities/by-stage');
  }

  async getCRMOpportunityMetrics() {
    return this.api.get('/crm/opportunities/metrics');
  }

  async getCRMOpportunity(id: string) {
    return this.api.get(`/crm/opportunities/${id}`);
  }

  async createCRMOpportunity(data: any) {
    return this.api.post('/crm/opportunities', data);
  }

  async updateCRMOpportunity(id: string, data: any) {
    return this.api.patch(`/crm/opportunities/${id}`, data);
  }

  async deleteCRMOpportunity(id: string) {
    return this.api.delete(`/crm/opportunities/${id}`);
  }

  async getCRMContacts() {
    return this.api.get('/crm/contacts');
  }

  async getCRMContact(id: string) {
    return this.api.get(`/crm/contacts/${id}`);
  }

  async createCRMContact(data: any) {
    return this.api.post('/crm/contacts', data);
  }

  async updateCRMContact(id: string, data: any) {
    return this.api.patch(`/crm/contacts/${id}`, data);
  }

  async deleteCRMContact(id: string) {
    return this.api.delete(`/crm/contacts/${id}`);
  }

  async getCRMActivities() {
    return this.api.get('/crm/activities');
  }

  async getCRMActivity(id: string) {
    return this.api.get(`/crm/activities/${id}`);
  }

  async createCRMActivity(data: any) {
    return this.api.post('/crm/activities', data);
  }

  async updateCRMActivity(id: string, data: any) {
    return this.api.patch(`/crm/activities/${id}`, data);
  }

  async deleteCRMActivity(id: string) {
    return this.api.delete(`/crm/activities/${id}`);
  }

  // MRR endpoints
  async getMRRItems() {
    return this.api.get('/mrr');
  }

  async createMRRItem(data: any) {
    return this.api.post('/mrr', data);
  }

  async updateMRRItem(id: string, data: any) {
    return this.api.patch(`/mrr/${id}`, data);
  }

  async deleteMRRItem(id: string) {
    return this.api.delete(`/mrr/${id}`);
  }

  async getTotalMRR() {
    return this.api.get('/mrr/total');
  }

  async getUpcomingMRRPayments(days?: number) {
    return this.api.get('/mrr/upcoming-payments', {
      params: days ? { days } : undefined
    });
  }

  // Expense endpoints
  async getExpenses(month?: string) {
    return this.api.get('/expenses', {
      params: month ? { month } : undefined
    });
  }

  async getExpensesSummary(month?: string) {
    return this.api.get('/expenses/summary', {
      params: month ? { month } : undefined
    });
  }

  async createExpense(data: any) {
    return this.api.post('/expenses', data);
  }

  async updateExpense(id: string, data: any) {
    return this.api.patch(`/expenses/${id}`, data);
  }

  async deleteExpense(id: string) {
    return this.api.delete(`/expenses/${id}`);
  }

  // Time tracking endpoints
  async createTimeEntry(data: any) {
    return this.api.post('/time-tracking', data);
  }

  async getTimeEntries(userId?: string, startDate?: Date, endDate?: Date, page: number = 1, limit: number = 100) {
    // First try with pagination params
    try {
      const params: any = {};
      if (userId) params.userId = userId;
      if (startDate) params.startDate = startDate.toISOString();
      if (endDate) params.endDate = endDate.toISOString();
      params.page = page;
      params.limit = limit;
      return await this.api.get('/time-tracking', { params });
    } catch (error: any) {
      // If it fails, try without pagination (old API)
      if (error.response?.status === 400 || error.response?.status === 500) {
        const params: any = {};
        if (userId) params.userId = userId;
        return this.api.get('/time-tracking', { params });
      }
      throw error;
    }
  }

  async getTodayTimeEntries() {
    return this.api.get('/time-tracking/today');
  }

  async getMonthlyHours(year?: number, month?: number) {
    const params: any = {};
    if (year) params.year = year;
    if (month) params.month = month;
    return this.api.get('/time-tracking/monthly-hours', { params });
  }

  async getWeeklyTimeStats() {
    return this.api.get('/time-tracking/weekly-stats');
  }

  async updateTimeEntry(id: string, data: any) {
    return this.api.patch(`/time-tracking/${id}`, data);
  }

  async deleteTimeEntry(id: string) {
    return this.api.delete(`/time-tracking/${id}`);
  }

  async startTimer(data: any) {
    return this.api.post('/time-tracking/start', data);
  }

  async stopTimer() {
    return this.api.post('/time-tracking/stop');
  }

  async getActiveTimer() {
    return this.api.get('/time-tracking/active');
  }

  // Sales endpoints
  async getShops() {
    return this.api.get('/sales/shops');
  }

  async getShop(id: string) {
    return this.api.get(`/sales/shops/${id}`);
  }

  async createShop(data: any) {
    return this.api.post('/sales/shops', data);
  }

  async updateShop(id: string, data: any) {
    return this.api.patch(`/sales/shops/${id}`, data);
  }

  async deleteShop(id: string) {
    return this.api.delete(`/sales/shops/${id}`);
  }

  async createSalesStat(data: any) {
    return this.api.post('/sales/stats', data);
  }

  async getAllSalesStats(startDate?: string, endDate?: string) {
    const params: any = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    return this.api.get('/sales/stats', { params });
  }

  async getSalesStats(shopId: string, startDate?: string, endDate?: string) {
    const params: any = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    return this.api.get(`/sales/stats/shop/${shopId}`, { params });
  }

  async updateSalesStat(id: string, data: any) {
    return this.api.patch(`/sales/stats/${id}`, data);
  }

  async deleteSalesStat(id: string) {
    return this.api.delete(`/sales/stats/${id}`);
  }

  async getSalesTotalDashboard(startDate?: string, endDate?: string) {
    const params: any = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    return this.api.get('/sales/dashboard/total', { params });
  }

  async getShopPerformance(startDate?: string, endDate?: string) {
    const params: any = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    return this.api.get('/sales/dashboard/performance', { params });
  }

  // Ad Account endpoints
  async getAdAccounts() {
    return this.api.get('/sales/ad-accounts');
  }

  async createAdAccount(data: any) {
    return this.api.post('/sales/ad-accounts', data);
  }

  async updateAdAccount(id: string, data: any) {
    return this.api.patch(`/sales/ad-accounts/${id}`, data);
  }

  async deleteAdAccount(id: string) {
    return this.api.delete(`/sales/ad-accounts/${id}`);
  }

  // Requests endpoints
  async getRequests(status?: string) {
    if (USE_MOCK_API) {
      return createMockResponse([]);
    }
    const params = status ? { status } : {};
    return this.api.get('/requests', { params });
  }

  async getRequest(id: string) {
    return this.api.get(`/requests/${id}`);
  }

  async createRequest(data: any) {
    return this.api.post('/requests', data);
  }

  async updateRequest(id: string, data: any) {
    return this.api.patch(`/requests/${id}`, data);
  }

  async deleteRequest(id: string) {
    return this.api.delete(`/requests/${id}`);
  }

  async deleteAllRequests() {
    return this.api.delete('/requests/delete-all');
  }

  async approveRequest(id: string, reviewNotes?: string) {
    return this.api.patch(`/requests/${id}/approve`, { reviewNotes });
  }

  async denyRequest(id: string, reviewNotes?: string) {
    return this.api.patch(`/requests/${id}/deny`, { reviewNotes });
  }

  async getRequestStats() {
    return this.api.get('/requests/stats');
  }

  // Generic request method with cache config
  async request<T = any>(config: AxiosRequestConfig & { cacheConfig?: CacheConfig }): Promise<AxiosResponse<T>> {
    return this.api.request(config);
  }

  // Generic patch method
  async patch<T = any>(url: string, data?: any): Promise<AxiosResponse<T>> {
    return this.api.patch(url, data);
  }

  // Cache management methods
  clearCache(pattern?: string): void {
    if (pattern) {
      cacheService.invalidate(pattern);
    } else {
      cacheService.clear();
    }
    console.log('Cache cleared:', pattern || 'all');
  }

  getCacheStats() {
    return cacheService.getStats();
  }

  // Method to bypass cache for specific request
  async getWithoutCache(url: string, config?: AxiosRequestConfig) {
    return this.api.get(url, {
      ...config,
      headers: {
        ...config?.headers,
        'X-Bypass-Cache': 'true',
      },
    });
  }

  // Method to get with custom cache TTL
  async getWithCustomTTL(url: string, ttl: number, config?: AxiosRequestConfig) {
    return this.api.get(url, {
      ...config,
      cacheConfig: { ttl },
    } as any);
  }

  // Notifications endpoints
  async getNotifications() {
    return this.api.get('/notifications');
  }

  async markNotificationAsRead(id: string) {
    return this.api.patch(`/notifications/${id}/read`);
  }

  async clearAllNotifications() {
    return this.api.delete('/notifications/clear-all');
  }

  // Health check
  async checkHealth() {
    try {
      const response = await this.api.get('/health');
      console.log('API Health Check:', response.data);
      return response.data;
    } catch (error) {
      console.error('API Health Check Failed:', error);
      throw error;
    }
  }

  // Sourcing endpoints
  async getSourcingItems() {
    return this.api.get('/sourcing');
  }

  async getSourcingItemsByProject(projectId: string) {
    return this.api.get(`/sourcing/project/${projectId}`);
  }

  async getSourcingItem(id: string) {
    return this.api.get(`/sourcing/${id}`);
  }

  async createSourcingItem(data: any) {
    return this.api.post('/sourcing', data);
  }

  async updateSourcingItem(id: string, data: any) {
    return this.api.patch(`/sourcing/${id}`, data);
  }

  async deleteSourcingItem(id: string) {
    return this.api.delete(`/sourcing/${id}`);
  }

  async uploadSourcingImage(id: string, file: File) {
    const formData = new FormData();
    formData.append('file', file);
    return this.api.post(`/sourcing/${id}/image`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  // News endpoints
  async getNews() {
    return this.api.get('/news');
  }

  async getLatestNews(limit?: number) {
    const params = limit ? { limit } : {};
    return this.api.get('/news/latest', { params });
  }

  async getNewsItem(id: string) {
    return this.api.get(`/news/${id}`);
  }

  async createNews(data: any) {
    return this.api.post('/news', data);
  }

  async updateNews(id: string, data: any) {
    return this.api.patch(`/news/${id}`, data);
  }

  async deleteNews(id: string) {
    return this.api.delete(`/news/${id}`);
  }

  // User permissions
  async updateUserPermissions(userId: string, permissions: any) {
    return this.api.patch(`/users/${userId}/permissions`, permissions);
  }

  // Research endpoints
  async getResearchItems() {
    return this.api.get('/research');
  }

  async getResearchItem(id: string) {
    return this.api.get(`/research/${id}`);
  }

  async createResearchItem(data: any) {
    return this.api.post('/research', data);
  }

  async updateResearchItem(id: string, data: any) {
    return this.api.patch(`/research/${id}`, data);
  }

  async deleteResearchItem(id: string) {
    return this.api.delete(`/research/${id}`);
  }

  // Inspiration endpoints
  async getInspirationItems() {
    return this.api.get('/inspiration');
  }

  async getInspirationItem(id: string) {
    return this.api.get(`/inspiration/${id}`);
  }

  async createInspirationItem(data: any) {
    return this.api.post('/inspiration', data);
  }

  async updateInspirationItem(id: string, data: any) {
    return this.api.patch(`/inspiration/${id}`, data);
  }

  async deleteInspirationItem(id: string) {
    return this.api.delete(`/inspiration/${id}`);
  }

  async uploadInspirationImage(file: File) {
    const formData = new FormData();
    formData.append('file', file);
    return this.api.post('/inspiration/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  // Creatives endpoints
  async getCreatives(filters?: { shopId?: string; type?: string; quality?: string }) {
    const params = new URLSearchParams();
    if (filters?.shopId) params.append('shopId', filters.shopId);
    if (filters?.type) params.append('type', filters.type);
    if (filters?.quality) params.append('quality', filters.quality);
    
    const queryString = params.toString();
    return this.api.get(`/creatives${queryString ? `?${queryString}` : ''}`);
  }

  async getCreative(id: string) {
    return this.api.get(`/creatives/${id}`);
  }

  async createCreative(data: any) {
    return this.api.post('/creatives', data);
  }

  async updateCreative(id: string, data: any) {
    return this.api.patch(`/creatives/${id}`, data);
  }

  async deleteCreative(id: string) {
    return this.api.delete(`/creatives/${id}`);
  }

  // Labels endpoints
  async getLabels() {
    return this.api.get('/labels');
  }

  async getLabel(id: string) {
    return this.api.get(`/labels/${id}`);
  }

  async createLabel(data: { name: string; color: string }) {
    return this.api.post('/labels', data);
  }

  async updateLabel(id: string, data: { name?: string; color?: string }) {
    return this.api.patch(`/labels/${id}`, data);
  }

  async deleteLabel(id: string) {
    return this.api.delete(`/labels/${id}`);
  }
}

export const apiService = new ApiService();